import styled, { css } from "styled-components";

import { BaseButton } from "../../components/Button";
import { QUERIES, SPACING, TOUCH_TARGET } from "../../utils/constants";

export type Tone = "critical" | "warning" | "healthy" | "unknown" | "neutral";

export const TONE_COLORS: Record<
  Tone,
  { color: string; background: string; border: string }
> = {
  critical: {
    color: "var(--color-status-error)",
    background: "var(--color-status-error-background-soft)",
    border: "var(--color-status-error-border-soft)",
  },
  warning: {
    color: "var(--color-status-warning)",
    background: "var(--color-status-warning-background-soft)",
    border: "var(--color-status-warning-border-soft)",
  },
  healthy: {
    color: "var(--color-status-success)",
    background: "var(--color-status-success-background-soft)",
    border: "var(--color-status-success-border-soft)",
  },
  unknown: {
    color: "var(--color-text-secondary)",
    background: "var(--color-background-3)",
    border: "var(--color-border)",
  },
  neutral: {
    color: "var(--color-text-default)",
    background: "var(--color-background-2)",
    border: "var(--color-border-light)",
  },
};

export const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  max-width: 1100px;
  margin: 0 auto;
  padding: ${SPACING.md}px;

  @media ${QUERIES.tabletLargeAndUp} {
    gap: ${SPACING.lg}px;
    padding: ${SPACING.lg}px;
  }
`;

export const PageHeading = styled.h1`
  margin: 0;
  font-size: var(--text-xxl);
  color: var(--color-text-default);
`;

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
  background: var(--color-card-background);
  color: var(--color-text-default);
`;

export const CardHeader = styled.header`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  flex-wrap: wrap;
`;

export const CardTitle = styled.h2`
  margin: 0;
  font-size: var(--text-lg);
`;

export const Caption = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const Banner = styled.section<{ $tone: Tone }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;
  border-radius: 12px;
  border: 1px solid ${({ $tone }) => TONE_COLORS[$tone].border};
  background: ${({ $tone }) => TONE_COLORS[$tone].background};
  color: var(--color-text-default);
`;

export const BannerTitle = styled.h2`
  margin: 0;
  font-size: var(--text-lg);
`;

export const DefinitionList = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${SPACING.xs}px ${SPACING.md}px;
  margin: 0;
  font-size: var(--text-base);

  dt {
    color: var(--color-text-secondary);
  }

  dd {
    margin: 0;
    font-weight: 600;
    text-align: right;
  }
`;

export const QueueLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${SPACING.md}px;
  align-items: start;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  }
`;

export const QueueList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

// A capped list runs far past the viewport, so it scrolls alone to keep the detail beside it.
export const ScrollingQueueList = styled(QueueList)`
  @media ${QUERIES.tabletLargeAndUp} {
    max-height: 70vh;
    padding: ${SPACING.xs}px;
    overflow-y: auto;
  }
`;

export const QueueRow = styled(BaseButton)<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${SPACING.xxs}px;
  width: 100%;
  padding: ${SPACING.sm}px;
  border: 1px solid
    ${({ $isSelected }) =>
      $isSelected ? "var(--color-primary)" : "var(--color-border)"};
  border-radius: 8px;
  background: var(--color-card-background);
  color: var(--color-text-default);
  font-size: var(--text-base);
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
`;

export const QueueRowSymbol = styled.span`
  font-weight: 600;
`;

export const QueueRowMeta = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const PageHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  flex-wrap: wrap;
`;

export const PageTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const SectionNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  flex-wrap: wrap;
  padding: ${SPACING.sm}px;
  border: 1px solid ${TONE_COLORS.warning.border};
  border-radius: 8px;
  background: ${TONE_COLORS.warning.background};
  font-size: var(--text-base);
`;

export const ActionLink = styled.a`
  color: var(--color-primary);
  font-weight: 600;

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px;
`;

export const ToolbarSearch = styled.div`
  flex: 1 1 240px;
  min-width: 0;
`;

export const ToolbarField = styled.label`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const ToolbarSelect = styled.select`
  min-height: ${TOUCH_TARGET}px;
  padding: 0 ${SPACING.xs}px;
  border: 1px solid var(--color-gray-border);
  border-radius: 8px;
  background: var(--color-card-background);
  color: var(--color-select-text);
  font-size: var(--text-base);

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
`;

export const ToolbarCount = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  white-space: nowrap;
`;

export const BackLink = styled.div`
  font-size: var(--text-base);
`;

export const RowHeading = styled.span`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.xs}px;
  flex-wrap: wrap;
`;

export const StatusNote = styled.p<{ $tone: Tone }>`
  margin: 0;
  padding: ${SPACING.sm}px;
  border: 1px solid ${({ $tone }) => TONE_COLORS[$tone].border};
  border-radius: 8px;
  background: ${({ $tone }) => TONE_COLORS[$tone].background};
  font-size: var(--text-base);
`;

export const DetailCard = styled(Card)`
  &:focus {
    outline: none;
  }
`;

const pillTone = (tone: Tone) => css`
  color: ${TONE_COLORS[tone].color};
  background: ${TONE_COLORS[tone].background};
  border: 1px solid ${TONE_COLORS[tone].border};
`;

export const Pill = styled.span<{ $tone: Tone }>`
  display: inline-block;
  align-self: flex-start;
  padding: ${SPACING.xxs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-sm);
  font-weight: 600;
  white-space: nowrap;
  ${({ $tone }) => pillTone($tone)}
`;

export const RowPills = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${SPACING.xxs}px;
`;

export const DraftNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;
